import { AxiosError, AxiosResponse } from 'axios';

export interface ApiErrorHandler {
    showError: (message: string, title?: string, variant?: 'critical' | 'warning' | 'info') => void;
    showSuccess: (message: string, title?: string) => void;
}

let globalErrorHandler: ApiErrorHandler | null = null;

export const setGlobalErrorHandler = (handler: ApiErrorHandler) => {
    globalErrorHandler = handler;
};

export const getGlobalErrorHandler = (): ApiErrorHandler | null => {
    return globalErrorHandler;
};

export const handleApiError = (error: AxiosError, customMessage?: string) => {
    if (!globalErrorHandler) {
        console.error('Global error handler not initialized:', error);
        return;
    }

    const response = error.response?.status;
    let message = customMessage || 'An unexpected error occurred';
    let variant: 'critical' | 'warning' | 'info' = 'critical';
    if (response) {
        // Handle specific status codes
        switch (response) {
            case 400:
                message = 'Invalid request data';
                variant = 'warning';
                break;
            case 401:
                message = 'Authentication required. Please log in again.';
                variant = 'warning';
                break;
            case 403:
                message = 'Your request was blocked by our security rules.';
                variant = 'warning';
                break;
            case 404:
                message = 'The requested resource was not found.';
                variant = 'info';
                break;
            case 409:
                message = 'A conflict occurred. The resource may already exist.';
                variant = 'warning';
                break;
            case 422:
                message = 'The submitted data is invalid.';
                variant = 'warning';
                break;
            case 500:
                message = 'A server error occurred. Please try again later.';
                variant = 'critical';
                break;
            case 502:
                message = 'Service temporarily unavailable. Please try again later.';
                variant = 'warning';
                break;
            case 503:
                message = 'Service is currently under maintenance. Please try again later.';
                variant = 'warning';
                break;
            case 504:
                message = 'Request timeout. Please check your connection and try again.';
                variant = 'warning';
                break;
            default:
                message = `Request failed with status response code ${response}`;
                variant = 'critical';
        }
    } else if (error.request) {
        // Network error
        message = 'Network error. Please check your internet connection.';
        variant = 'warning';
    } else {
        // Other error
        message = error.message || 'An unexpected error occurred';
        variant = 'critical';
    }

    globalErrorHandler.showError(message, undefined, variant);
};

export const handleApiSuccess = (response: AxiosResponse, customMessage?: string) => {
    if (!globalErrorHandler) {
        return;
    }

    if (customMessage) {
        globalErrorHandler.showSuccess(customMessage);
    }
};

// Network error messages
export const NetworkError = {
    CONNECTION_LOST: 'Connection lost. Please check your internet connection.',
    TIMEOUT: 'Request timed out. Please try again.',
    SERVER_UNREACHABLE: 'Unable to reach the server. Please try again later.',
};

/**
 * Extracts a readable error message from various error object formats
 * @param error - The error object (could be AxiosError, Error, or any object)
 * @param fallbackMessage - Default message if no specific message is found
 * @returns A human-readable error message string
 */
export const getErrorMessage = (error: any, fallbackMessage: string = 'An error occurred'): string => {
    // Handle axios error response structure
    if (error?.response?.data?.message) {
        return error.response.data.message;
    }
    
    // Handle standard Error objects
    if (error?.message) {
        return error.message;
    }
    
    // Handle string errors
    if (typeof error === 'string') {
        return error;
    }
    
    // Handle objects with error property
    if (error?.error) {
        return getErrorMessage(error.error, fallbackMessage);
    }
    
    return fallbackMessage;
};
